import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getBirthdays, celebrateBirthday } from '@/api/owner';
import { Gift, Cake, Send, CheckCircle2, AlertCircle, X, Users } from 'lucide-react';

const unwrap = (r: any) => r?.data?.data ?? r?.data;
const fmtDate = (d?: string | null) => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'long' }) : '—';

export function BirthdaysPage() {
  const [range, setRange] = useState<'today' | 'week' | 'month'>('week');
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  useEffect(() => { if (successMsg) { const t = setTimeout(() => setSuccessMsg(null), 4000); return () => clearTimeout(t); } }, [successMsg]);
  useEffect(() => { if (error) { const t = setTimeout(() => setError(null), 6000); return () => clearTimeout(t); } }, [error]);

  const birthdaysQ = useQuery({ 
    queryKey: ['owner-birthdays', range], 
    queryFn: () => getBirthdays({ range }).then(unwrap) 
  });

  const people = birthdaysQ.data?.people ?? [];
  
  // Sort: Today first, then by date
  const sorted = [...people].sort((a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime());
  const todayStr = new Date().toISOString().split('T')[0];
  
  const today = sorted.filter((p: any) => p.date === todayStr);
  const upcoming = sorted.filter((p: any) => p.date !== todayStr);

  return (
    <div className="max-w-[1400px] mx-auto p-6 space-y-6">
      {error && <div className="fixed top-4 right-4 z-[100] max-w-md bg-red-600 text-white rounded-xl shadow-2xl p-4 flex items-start gap-3"><AlertCircle className="w-5 h-5 flex-shrink-0" /><p className="text-sm font-semibold flex-1">{error}</p><button onClick={() => setError(null)}><X className="w-4 h-4" /></button></div>}
      {successMsg && <div className="fixed top-4 right-4 z-[100] max-w-md bg-green-600 text-white rounded-xl shadow-2xl p-4 flex items-start gap-3"><CheckCircle2 className="w-5 h-5 flex-shrink-0" /><p className="text-sm font-semibold flex-1">{successMsg}</p><button onClick={() => setSuccessMsg(null)}><X className="w-4 h-4" /></button></div>}

      <div className="flex justify-between items-end flex-wrap gap-3">
        <div>
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">COMMUNICATION</p>
          <h1 className="text-3xl font-extrabold text-slate-900">Birthdays</h1>
          <p className="text-slate-500 text-sm mt-1">Celebrate students and staff birthdays</p>
        </div>
        <div className="flex border border-slate-300 rounded-lg overflow-hidden bg-white">
          {(['today', 'week', 'month'] as const).map(r => (
            <button key={r} onClick={() => setRange(r)} className={`px-4 py-2 text-sm font-bold capitalize transition-colors ${range === r ? 'bg-[#0f172a] text-white' : 'text-slate-600 hover:bg-slate-50'}`}>
              {r === 'today' ? 'Today' : r === 'week' ? 'This Week' : 'This Month'}
            </button>
          ))}
        </div>
      </div>

      {birthdaysQ.isLoading ? (
        <p className="text-sm text-slate-500 text-center py-8">Loading birthdays…</p>
      ) : people.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center shadow-sm">
          <Cake className="w-10 h-10 text-slate-300 mx-auto mb-2" />
          <p className="font-semibold text-slate-700">No birthdays found</p>
          <p className="text-sm text-slate-500 mt-1">No one is celebrating in this period.</p>
        </div>
      ) : (
        <div className="space-y-8">
          {today.length > 0 && (
            <section>
              <div className="flex items-center gap-2 mb-3">
                <Gift className="w-5 h-5 text-pink-600" />
                <h2 className="text-lg font-bold text-slate-900">Today 🎉</h2>
                <span className="text-xs font-bold bg-pink-100 text-pink-700 px-2 py-0.5 rounded-full">{today.length}</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {today.map((p: any) => (
                  <BirthdayCard key={`${p.kind}-${p.student_id ?? p.user_id}`} person={p} highlight onSuccess={() => setSuccessMsg('Greeting sent!')} onError={setError} />
                ))}
              </div>
            </section>
          )}

          {upcoming.length > 0 && (
            <section>
              <div className="flex items-center gap-2 mb-3">
                <Users className="w-5 h-5 text-slate-400" />
                <h2 className="text-lg font-bold text-slate-900">Upcoming</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {upcoming.map((p: any) => (
                  <BirthdayCard key={`${p.kind}-${p.student_id ?? p.user_id}`} person={p} onSuccess={() => setSuccessMsg('Greeting sent!')} onError={setError} />
                ))}
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
}

function BirthdayCard({ person, highlight, onSuccess, onError }: { person: any; highlight?: boolean; onSuccess: () => void; onError: (m: string) => void }) {
  const [sending, setSending] = useState(false);
  
  // Determine if celebrated (backend returns true/false)
  const isDone = person.celebrated;

  const send = async () => {
    setSending(true);
    try {
      await celebrateBirthday({
        student_id: person.kind === 'student' ? person.student_id : null,
        user_id: person.kind === 'staff' ? person.user_id : null,
        message: `Happy birthday ${person.name}! 🎉`,
      });
      onSuccess();
    } catch (e: any) {
      onError(e?.response?.data?.message ?? 'Failed to send greeting');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className={`rounded-xl border p-4 shadow-sm flex items-center gap-3 ${highlight ? 'bg-gradient-to-r from-pink-50 to-violet-50 border-pink-200' : 'bg-white border-slate-200'}`}>
      <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${highlight ? 'bg-pink-200 text-pink-700' : 'bg-slate-100 text-slate-600'}`}>
        <Cake className="w-6 h-6" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-bold text-slate-900 truncate">{person.name}</p>
        <p className="text-xs text-slate-500 capitalize">
          {person.kind} {person.turning ? `· Turning ${person.turning}` : ''} · {fmtDate(person.date)}
        </p>
      </div>
      <button 
        onClick={send} 
        disabled={sending || isDone} 
        className={`px-3 py-2 rounded-lg text-xs font-bold transition inline-flex items-center gap-1 ${
          isDone ? 'bg-slate-100 text-slate-400 cursor-not-allowed' :
          highlight ? 'bg-pink-600 text-white hover:bg-pink-700' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
        } disabled:opacity-50`}
      >
        {isDone ? <><CheckCircle2 className="w-3 h-3" /> Sent</> : <><Send className="w-3 h-3" /> {sending ? '…' : highlight ? 'Send greeting' : 'Greet'}</>}
      </button>
    </div>
  );
}
